import { useEffect } from "react";
import { Clock, Users, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "../components/Reveal";
import { COURSES, EMAIL } from "../data/content";

export default function Courses() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main data-testid="courses-page" className="pt-28 sm:pt-36 pb-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>The Academy</Eyebrow>
          <h1 className="font-serif-ed font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1.08] mt-6 text-[#2A1F16]">
            Learn the <em className="text-[#96754D]">art</em> of makeup
          </h1>
          <p className="mt-6 text-[15px] sm:text-base leading-relaxed text-[#6B5A49]">
            Small batches, taught personally by Mrinalee — whether you want to master your own
            everyday glam or begin your journey as a professional artist.
          </p>
        </Reveal>

        <div className="mt-14 grid md:grid-cols-2 gap-6">
          {COURSES.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.1} className="h-full">
              <article
                data-testid={`course-card-${i + 1}`}
                className="group h-full bg-[#FFFCF7] border border-[#E9DCC8] rounded-2xl overflow-hidden flex flex-col hover:border-[#B8893B]/50 hover:shadow-[0_24px_60px_-30px_rgba(42,31,22,0.25)] transition-[border-color,box-shadow] duration-500"
              >
                <div className="relative overflow-hidden h-60">
                  <img
                    src={c.img}
                    alt={c.alt}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1.2s] ease-out"
                  />
                  <div className="absolute top-4 left-4 flex gap-2">
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-[#FFFCF7]/85 backdrop-blur-md text-[#2A1F16] px-3.5 py-1.5 rounded-full">
                      {c.level}
                    </span>
                    <span className="text-[10px] uppercase tracking-[0.2em] bg-[#C9A25F] text-[#2A1F16] px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5">
                      <Clock size={11} />
                      {c.duration}
                    </span>
                  </div>
                </div>

                <div className="p-7 sm:p-8 flex flex-col flex-1">
                  <h2 className="font-serif-ed text-2xl sm:text-3xl text-[#2A1F16]">{c.title}</h2>
                  <p className="mt-1.5 font-serif-ed italic text-[#B8893B] text-lg">{c.tagline}</p>
                  <p className="mt-4 text-[15px] leading-relaxed text-[#6B5A49]">{c.copy}</p>

                  <p className="mt-7 text-[11px] uppercase tracking-[0.25em] text-[#96754D] font-medium">
                    What you'll learn
                  </p>
                  <ul className="mt-3 space-y-2.5 flex-1">
                    {c.learn.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-[15px] text-[#2A1F16]">
                        <span className="mt-[7px] h-1.5 w-1.5 rotate-45 bg-[#B8893B] shrink-0" aria-hidden="true" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8 pt-6 border-t border-[#E9DCC8] flex flex-wrap items-center justify-between gap-4">
                    <span className="text-xs text-[#9C8871]">Fees &amp; upcoming batches on enquiry</span>
                    <a
                      data-testid={`course-enquire-${i + 1}`}
                      href={`/#inquire`}
                      className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] bg-[#2A1F16] text-[#FBF6EE] px-6 py-3.5 rounded-full hover:bg-[#96754D] transition-colors duration-300"
                    >
                      Enquire for this course
                      <ArrowUpRight size={14} />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.15}>
          <div className="mt-14 border border-[#E9DCC8] rounded-2xl bg-[#F4EBDD] p-7 sm:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
            <div className="flex items-start gap-4">
              <span className="h-11 w-11 rounded-full bg-[#C9A25F]/25 text-[#96754D] flex items-center justify-center shrink-0">
                <Users size={18} />
              </span>
              <p className="text-[15px] leading-relaxed text-[#6B5A49] max-w-xl">
                Every batch is intentionally small so each student gets personal attention. Group
                bookings and one-on-one formats are available — write to{" "}
                <a
                  data-testid="courses-email-link"
                  href={`mailto:${EMAIL}?subject=Course%20enquiry`}
                  className="text-[#2A1F16] underline decoration-[#B8893B] underline-offset-4 hover:text-[#96754D] transition-colors"
                >
                  {EMAIL}
                </a>{" "}
                for the next batch dates.
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
